import * as React from "react"
import {useContext, useEffect, useRef, useState} from "react"
import {Footer} from "nhsuk-react-components"
import {AppContext} from "../index"
import {axiosInstance} from "../requests/axiosInstance"
import styled from "styled-components"

interface Status {
  checks: Checks
}

interface Checks {
  eps: Array<Check>
  "signing-service": Array<Check>
  validator: Array<Check>
}

interface Check {
  version: string
}

interface SoftwareVersions {
  eps: string
  signingService: string
  validator: string
}

const StyledFooter = styled(Footer)`
  background: var(--nhsuk-grey-4-colour);
  border-top: var(--nhsuk-blue-colour) 4px solid;

  div {
    border: none;
  }
`

// Site navigation uses <a href> full page loads rather than client-side routing,
// so cache across page loads in sessionStorage to avoid refetching on every page
const SOFTWARE_VERSIONS_CACHE_KEY = "epsat_software_versions"

export const PageFooter: React.FC = () => {
  const {baseUrl} = useContext(AppContext)

  const [softwareVersions, setSoftwareVersions] = useState<SoftwareVersions>(() => {
    const cached = sessionStorage.getItem(SOFTWARE_VERSIONS_CACHE_KEY)
    return cached ? JSON.parse(cached) : undefined
  })
  // Synchronous guard: prevents duplicate _healthcheck calls if the effect
  // re-runs before the state update from the previous call has landed
  const hasFetched = useRef(false)

  useEffect(() => {
    if (hasFetched.current || softwareVersions) {
      return
    }
    hasFetched.current = true

    const abortController = new AbortController()

    ;(async () => {
      try {
        const statusResult = await (await axiosInstance.get<Status>(
          `${baseUrl}_healthcheck`,
          {signal: abortController.signal}
        )).data
        const versions: SoftwareVersions = {
          eps: statusResult.checks.eps[0].version,
          signingService: statusResult.checks["signing-service"][0].version,
          validator: statusResult.checks.validator[0].version
        }
        sessionStorage.setItem(SOFTWARE_VERSIONS_CACHE_KEY, JSON.stringify(versions))
        setSoftwareVersions(versions)
      } catch (error) {
        if (!abortController.signal.aborted) {
          throw error
        }
      }
    })()

    return () => abortController.abort()
  }, [baseUrl])

  return (
    <StyledFooter>
      {softwareVersions &&
        <Footer.List >
          <Footer.ListItem>EPS: {softwareVersions.eps}</Footer.ListItem>
          <Footer.ListItem>Signing-Service: {softwareVersions.signingService}</Footer.ListItem>
          <Footer.ListItem>Validator: {softwareVersions.validator}</Footer.ListItem>
        </Footer.List>
      }
      <Footer.Copyright>&copy; Crown copyright</Footer.Copyright>
    </StyledFooter>
  )
}
